/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, ChangeEvent } from 'react';
import dayjs from 'dayjs';
import {
  Sizes,
  Positions,
  Icons,
  TextInput,
  Card,
} from '@sede-enterprise/shell-ds-react-framework';
import { useSelector } from 'react-redux';
import { ActionMenu, NGContainer } from '../NominationGroups/style';
import Table from 'src/UI/Table';
import Button from 'src/UI/Button';
import { searchByText } from 'src/utils/common/utils';
import { NUMBER_20 } from 'src/utils/table';
import { get } from 'src/services/GenesisApi';
import { DOWNLOAD_CONTRACTS, GET_CONTRACTS, MESSAGES } from 'src/constants/EUMarkets';
import DownloadButton from 'src/UI/Download';
import { sendNotification } from 'src/UI/Notification';
import { IContract } from 'src/types/EUMarkets.type';
import Parameters from '../Parameters';
import { COMMON_MESSAGES } from 'src/constants/Common';
import { IFullState } from 'src/types/All.type';
import { rolePermissions } from 'src/constants/Permission';
import { getEnv } from 'src/utils/env.vite';
import ViewPositions from './ViewPositions';
import AddEditContract from './AddEdit';

const { ArrowCcw, Search, Add: AddIcon, EllipsisVertical } = Icons;

// Add/Modify are only available in the Dev environment until the save APIs are ready elsewhere.
const isDevEnvironment = () => getEnv('VITE_APP_ENVIRONMENT') === 'Dev';

const Contracts: React.FC = () => {
  const [contracts, setContracts] = useState<IContract[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [openPositions, setOpenPositions] = useState<boolean>(false);
  const [openAddEdit, setOpenAddEdit] = useState<boolean>(false);
  const [contract, setContract] = useState<IContract | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);

  const getData = () => {
    // API call to fetch data
    setLoading(true);
    get(GET_CONTRACTS)
      .then((response) => {
        setContracts(response.data);
      })
      .catch(() => {
        sendNotification({
          type: 'error',
          message: MESSAGES.SERVER_ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  const handleModalOpenClose = () => {
    if (open) {
      setContract(null);
    }
    setOpen(!open);
  };

  const handlePositionsModalOpenClose = () => {
    if (openPositions) {
      setContract(null);
    }
    setOpenPositions(!openPositions);
  };

  const handleAddEditOpenClose = () => {
    if (openAddEdit) {
      setContract(null);
    }
    setOpenAddEdit(!openAddEdit);
  };

  const handleEdit = (item: IContract) => {
    setContract(item);
    setOpenAddEdit(true);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const renderData = (text: string, data: IContract[]) => {
    return searchByText(data, text, [
      'contractName',
      'contractTypeName',
      'shortName',
      'internalOrganizationName',
      'externalOrganizationName',
    ]);
  };

  const handleShowParams = (item: IContract) => {
    setContract(item);
    setOpen(true);
  };

  const handleShowPositions = (item: IContract) => {
    setContract(item);
    setOpenPositions(true);
  };

  const renderActions = (item: IContract) => (
    <ActionMenu
      action='click'
      popup={
        <div className='menu-container'>
          {isDevEnvironment() && permissions.includes(rolePermissions.updateMasterData) && (
            <Button
              data-testid={`btn-modify-${item.id}`}
              size='small'
              onClick={() => handleEdit(item)}
              variant='transparent'
            >
              Modify
            </Button>
          )}
        </div>
      }
    >
      <Button
        size='small'
        data-testid={`btn-actions-${item.id}`}
        variant='transparent'
        onClick={() => {}}
        icon={<EllipsisVertical />}
        iconOnly
      />
    </ActionMenu>
  );

  const showActions = isDevEnvironment() && permissions.includes(rolePermissions.updateMasterData);

  const columns = [
    {
      accessKey: 'contractName',
      fieldName: 'contractName',
      title: 'Contract Name',
      sorting: true,
    },
    {
      accessKey: 'dealId',
      fieldName: 'dealId',
      title: 'Deal Id',
      sorting: true,
    },
    {
      accessKey: 'contractTypeName',
      fieldName: 'contractTypeName',
      title: 'Contract Type',
      sorting: true,
    },

    {
      accessKey: 'aliasName',
      fieldName: 'aliasName',
      title: 'Alias Name',
      sorting: true,
    },
    {
      accessKey: 'internalLegalEntity',
      fieldName: 'internalLegalEntity',
      title: 'Internal Legal Entity',
      sorting: true,
    },
    {
      accessKey: 'externalLegalEntity',
      fieldName: 'externalLegalEntity',
      title: 'External Legal Entity',
      sorting: true,
    },
    {
      accessKey: 'validFrom',
      fieldName: 'validFrom',
      title: 'Valid From',
      render: (item) =>
        item.validFrom
          ? dayjs(item.validFrom).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY)
          : 'No Expiry',
    },
    {
      accessKey: 'validTo',
      fieldName: 'validTo',
      title: 'Valid To',
      render: (item) =>
        item.validTo ? dayjs(item.validTo).format(COMMON_MESSAGES.DATE_DD_MMM_YYYY) : 'No Expiry',
    },
    {
      accessKey: 'positions',
      fieldName: 'positions',
      title: 'View Positions',
      render: (item) => (
        <Button
          data-testid={`btn-positions-${item.id}`}
          size='small'
          onClick={() => handleShowPositions(item)}
          key={`tsk-positions-btn-${item.id}`}
          variant='transparent'
          className='logs-info'
        >
          View
        </Button>
      ),
    },
    {
      accessKey: 'parameters',
      fieldName: 'parameters',
      title: 'Parameters',
      render: (item) => (
        <Button
          data-testid={`btn-params-${item.id}`}
          size='small'
          onClick={() => handleShowParams(item)}
          key={`tsk-add-btn-${item.id}`}
          variant='transparent'
          className='logs-info'
        >
          View
        </Button>
      ),
    },
    ...(showActions
      ? [
          {
            accessKey: 'actions',
            fieldName: 'actions',
            title: 'Actions',
            render: (item: IContract) => renderActions(item),
          },
        ]
      : []),
  ];

  return (
    <NGContainer>
      <div className='action-container'>
        <div className='search-container'>
          <TextInput
            placeholder='Search'
            onChange={handleSearch}
            suffix={{
              node: <Search height={24} width={24} />,
            }}
            size={Sizes.Small}
          />
        </div>
        {isDevEnvironment() && permissions.includes(rolePermissions.createMasterData) && (
          <Button
            onClick={handleAddEditOpenClose}
            data-testid='add-new-contract'
            icon={<AddIcon />}
            size={Sizes.Small}
            variant='outlined'
            iconPosition={Positions.Right}
          >
            Add
          </Button>
        )}
        <DownloadButton
          downloadURL={DOWNLOAD_CONTRACTS}
          method='GET'
          fileName='Contracts'
          fileType='xlsx'
          buttonProp={{
            size: Sizes.Small,
            variant: 'outlined',
            className: 'master-data-download master-data-download-secondary',
            iconOnly: true,
            'aria-label': 'Download Contracts as Excel',
            title: 'Download Contracts as Excel',
          }}
        />
        <Button
          size='small'
          iconOnly
          icon={<ArrowCcw />}
          onClick={getData}
          disabled={loading}
          data-testid='btn-refresh'
        />
      </div>
      <Card className='table-card'>
        <Table
          data={renderData(searchText, contracts)}
          columns={columns}
          totalRecords={renderData(searchText, contracts).length}
          recordsPerPage={NUMBER_20}
          isLoading={loading}
        />
      </Card>
      {open && (
        <Parameters
          open={open}
          onClose={handleModalOpenClose}
          params={{
            name: contract?.contractName ?? '',
            parameters: contract?.parameters ?? [],
          }}
          paramType='Contract'
        />
      )}
      {openPositions && (
        <ViewPositions
          open={openPositions}
          onClose={handlePositionsModalOpenClose}
          positions={contract?.positions ?? []}
          contractName={contract?.contractName ?? ''}
        />
      )}
      <AddEditContract
        open={openAddEdit}
        onClose={handleAddEditOpenClose}
        contract={contract}
        refreshData={getData}
      />
    </NGContainer>
  );
};

export default Contracts;
